import { supabase } from '@/integrations/supabase/client';

export interface DeviceFingerprint {
  fingerprint: string;
  deviceName: string;
  userAgent: string;
}

export interface SecurityEvent {
  userId?: string;
  eventType: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  ipAddress?: string;
  userAgent?: string;
  details?: Record<string, any>;
}

// Simple hash function for fingerprint
async function hashString(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Generate device fingerprint from browser characteristics
export async function generateDeviceFingerprint(): Promise<DeviceFingerprint> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  let canvasFingerprint = '';
  
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint', 2, 2);
    canvasFingerprint = canvas.toDataURL();
  }

  const rawData = JSON.stringify({
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenResolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    canvas: canvasFingerprint.slice(0, 100),
  });

  // Hash to fixed 64-character string (SHA-256 hex)
  const fingerprint = await hashString(rawData);

  return {
    fingerprint,
    deviceName: `${navigator.platform} - ${navigator.userAgent.slice(0, 50)}`,
    userAgent: navigator.userAgent,
  };
}

// Track device on login
export async function trackDevice(userId: string) {
  const device = await generateDeviceFingerprint();
  
  const { data, error } = await supabase
    .from('user_devices')
    .upsert({
      user_id: userId,
      device_fingerprint: device.fingerprint,
      device_name: device.deviceName,
      user_agent: device.userAgent,
      last_seen_at: new Date().toISOString(),
    }, {
      onConflict: 'user_id,device_fingerprint'
    })
    .select()
    .single();

  if (error) throw error;
  
  // Check device limit (max 3 devices)
  const { data: devices } = await supabase
    .from('user_devices')
    .select('id')
    .eq('user_id', userId)
    .eq('is_blocked', false);

  if (devices && devices.length > 3) {
    await logSecurityEvent({
      userId,
      eventType: 'device_limit_exceeded',
      severity: 'high',
      details: { deviceCount: devices.length, limit: 3 },
    });
    throw new Error('Device limit exceeded. Please remove an old device.');
  }

  return data;
}

// Create session (limit to 1 concurrent session)
export async function createSession(userId: string, deviceId: string) {
  // Terminate all other active sessions (force single session)
  await supabase
    .from('user_sessions')
    .update({ is_active: false })
    .eq('user_id', userId)
    .eq('is_active', true);

  const sessionToken = crypto.randomUUID();
  const expiresAt = new Date();
  expiresAt.setHours(expiresAt.getHours() + 24);

  const { data, error } = await supabase
    .from('user_sessions')
    .insert({
      user_id: userId,
      device_id: deviceId,
      session_token: sessionToken,
      ip_address: 'unknown', // Set by edge function
      user_agent: navigator.userAgent,
      expires_at: expiresAt.toISOString(),
    })
    .select()
    .single();

  if (error) throw error;

  await logSecurityEvent({
    userId,
    eventType: 'session_created',
    severity: 'low',
    details: { sessionId: data.id },
  });

  return data;
}

// Update session activity
export async function updateSessionActivity(sessionToken: string) {
  await supabase
    .from('user_sessions')
    .update({ last_activity_at: new Date().toISOString() })
    .eq('session_token', sessionToken)
    .eq('is_active', true);
}

// Log security event
export async function logSecurityEvent(event: SecurityEvent) {
  await supabase.from('security_events').insert({
    user_id: event.userId,
    event_type: event.eventType,
    severity: event.severity,
    ip_address: event.ipAddress,
    user_agent: event.userAgent,
    details: event.details,
  });
}

// Check rate limit
export async function checkRateLimit(
  identifier: string,
  identifierType: 'user' | 'ip' | 'device',
  endpoint: string,
  maxRequests: number,
  windowMinutes: number
): Promise<boolean> {
  const windowStart = new Date();
  windowStart.setMinutes(windowStart.getMinutes() - windowMinutes);

  const { data: existing } = await supabase
    .from('rate_limits')
    .select('*')
    .eq('identifier', identifier)
    .eq('identifier_type', identifierType)
    .eq('endpoint', endpoint)
    .gte('window_start', windowStart.toISOString())
    .single();

  if (existing) {
    if (existing.is_blocked) return false;
    
    if (existing.request_count >= maxRequests) {
      await supabase
        .from('rate_limits')
        .update({ is_blocked: true })
        .eq('id', existing.id);
      
      await logSecurityEvent({
        eventType: 'rate_limit_exceeded',
        severity: 'medium',
        details: { identifier, endpoint, count: existing.request_count },
      });
      
      return false;
    }

    await supabase
      .from('rate_limits')
      .update({ request_count: existing.request_count + 1 })
      .eq('id', existing.id);
  } else {
    const windowEnd = new Date();
    windowEnd.setMinutes(windowEnd.getMinutes() + windowMinutes);

    await supabase.from('rate_limits').insert({
      identifier,
      identifier_type: identifierType,
      endpoint,
      request_count: 1,
      window_start: new Date().toISOString(),
      window_end: windowEnd.toISOString(),
    });
  }

  return true;
}

// Detect suspicious activity patterns
export async function detectAnomalies(userId: string): Promise<string[]> {
  const anomalies: string[] = [];

  // Check for multiple IPs in short timeframe
  const { data: recentSessions } = await supabase
    .from('user_sessions')
    .select('ip_address, started_at')
    .eq('user_id', userId)
    .gte('started_at', new Date(Date.now() - 3600000).toISOString());

  if (recentSessions) {
    const uniqueIps = new Set(recentSessions.map(s => s.ip_address));
    if (uniqueIps.size > 3) {
      anomalies.push('Multiple IP addresses detected');
      await logSecurityEvent({
        userId,
        eventType: 'multiple_ip_detected',
        severity: 'high',
        details: { ipCount: uniqueIps.size },
      });
    }
  }

  // Check for high activity rate (potential scraping)
  const { data: recentEvents } = await supabase
    .from('security_events')
    .select('id')
    .eq('user_id', userId)
    .gte('created_at', new Date(Date.now() - 60000).toISOString());

  if (recentEvents && recentEvents.length > 100) {
    anomalies.push('Unusually high activity detected');
    await logSecurityEvent({
      userId,
      eventType: 'high_activity_detected',
      severity: 'critical',
      details: { eventCount: recentEvents.length },
    });
  }

  return anomalies;
}
